import { useState } from "react";
import { useParams } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPut, apiErrorMessage } from "../../api/client";
import { usePersona } from "../../hooks/usePersona";
import { formatWhen } from "../../lib/when";
import { HoursByAgent } from "../../components/HoursByAgent";

/**
 * The Efficiency Ledger — hours returned, measured rather than claimed.
 *
 * The loudest claim this product makes, so the page is built to let someone
 * disprove it. Every line shows the baseline it multiplied, who declared that
 * baseline, when, and what they based it on; a decision nobody has declared a
 * baseline for reads "not measured" rather than zero, because zero is an
 * answer and "nobody has said what this used to take" is not.
 *
 * There is no projection anywhere on it. Hours returned are counted from
 * `ledger_entries`, one row per run that produced something.
 */

interface Line {
  hand_key: string;
  decision_key: string;
  runs: number;
  touched: number;
  retroactive?: number;
  minutes_returned: number;
  baseline_minutes: number | null;
  review_minutes: number | null;
  source_note: string | null;
  declared_by_name: string | null;
  declared_at: string | null;
  first_pass_pct: number | null;
}

interface DecisionRef {
  hand_key: string;
  hand_name: string;
  decision_key: string;
  decision_label: string;
}

interface LedgerResponse {
  lines: Line[];
  totals: { minutes_returned: number; runs: number; touched: number };
  decision_stats?: { resolved: number; median_minutes_to_decision: number | null; p90_minutes_to_decision: number | null; by_role: Record<string, number> };
  decisions: DecisionRef[];
}

function hours(minutes: number): string {
  const h = minutes / 60;
  if (Math.abs(h) < 10) return `${(Math.round(h * 10) / 10).toFixed(1)} h`;
  return `${Math.round(h)} h`;
}

function DeclareBaseline({
  workspaceId,
  decision,
  line,
}: {
  workspaceId: string;
  decision: DecisionRef;
  line: Line | undefined;
}) {
  const qc = useQueryClient();
  const [minutes, setMinutes] = useState(String(line?.baseline_minutes ?? ""));
  const [review, setReview] = useState(String(line?.review_minutes ?? "0"));
  const [note, setNote] = useState(line?.source_note ?? "");
  const save = useMutation({
    mutationFn: () =>
      apiPut(`/api/workspaces/${workspaceId}/ledger/baseline`, {
        hand_key: decision.hand_key,
        decision_key: decision.decision_key,
        minutes_per_run: Number(minutes),
        review_minutes: Number(review || 0),
        source_note: note,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ledger", workspaceId] }),
  });

  return (
    <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
      <label style={{ color: "var(--text-3)" }}>
        Took a person{" "}
        <input
          aria-label={`Baseline minutes for ${decision.decision_label}`}
          value={minutes}
          onChange={(e) => setMinutes(e.target.value)}
          placeholder="45" style={{ width: 64 }}
        />{" "}
        min
      </label>
      <label style={{ color: "var(--text-3)" }}>
        Review still takes{" "}
        <input
          aria-label={`Review minutes for ${decision.decision_label}`}
          value={review}
          onChange={(e) => setReview(e.target.value)}
          placeholder="5" style={{ width: 64 }}
        />{" "}
        min
      </label>
      <input
        aria-label={`Basis for ${decision.decision_label}`}
        placeholder="What is this based on? A time study, a manager's estimate…"
        value={note}
        onChange={(e) => setNote(e.target.value)} style={{ flex: "1 1 280px", minWidth: 220 }}
      />
      <button
        type="button"
        className="btn"
        data-testid={`declare-${decision.decision_key}`}
        disabled={save.isPending || !Number(minutes) || note.trim().length < 10}
        onClick={() => save.mutate()}
      >
        {save.isPending ? "Recording…" : line?.baseline_minutes ? "Update the baseline" : "Declare the baseline"}
      </button>
      {save.isError ? (
        <span className="pill red">{apiErrorMessage(save.error)}</span>
      ) : null}
    </div>
  );
}

function LedgerInner() {
  const { workspaceId = "" } = useParams();
  const persona = usePersona(workspaceId);
  const canDeclare = persona === "cxo";
  const q = useQuery<LedgerResponse, Error>({
    queryKey: ["ledger", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => apiGet(`/api/workspaces/${workspaceId}/ledger`),
  });

  const decisions = q.data?.decisions ?? [];
  const lines = q.data?.lines ?? [];
  const measured = decisions.filter((d) =>
    lines.some(
      (l) =>
        l.hand_key === d.hand_key &&
        l.decision_key === d.decision_key &&
        l.baseline_minutes !== null,
    ),
  );

  return (
    <div className="main-inner" data-testid="ledger">
      <div className="greet-row">
        <span className="greet">Efficiency Ledger</span>
      </div>
      <div className="subgreet">
        <div className="section-eyebrow">HOURS RETURNED</div>
        <div className="lede" data-testid="ledger-lede">
          {q.isLoading
            ? "Counting…"
            : measured.length === 0
              ? "Nothing is measured yet. Declare what a piece of work used to take and the ledger starts counting."
              : `${hours(q.data!.totals.minutes_returned)} returned across ${q.data!.totals.runs} ${
                  q.data!.totals.runs === 1 ? "run" : "runs"
                }.`}
        </div>
        {q.data?.decision_stats ? (
          <div className="lede" data-testid="ledger-decisions" style={{ marginTop: 4 }}>
            {q.data.decision_stats.resolved} {q.data.decision_stats.resolved === 1 ? "decision" : "decisions"} in the last 30 days
            {q.data.decision_stats.median_minutes_to_decision !== null
              ? ` · median wait ${waitWords(q.data.decision_stats.median_minutes_to_decision)}`
              : ""}
            .
          </div>
        ) : null}
        <div className="play-meta" style={{ marginTop: 6 }}>
          <span>
            Counted from the run records, once per day's work, and only for
            work that was used: released on its own, or approved by a person.
            A run stopped at the gate, a draft still waiting, and a draft a
            person rejected return nothing; a run a person signed returns the
            baseline minus the review time they declared.
          </span>
        </div>
      </div>

      <div style={{ marginTop: 18 }}>
        <HoursByAgent workspaceId={workspaceId} />
      </div>

      <div className="section-h">
        <span className="ttl">By decision</span>
        <span className="meta">each with the baseline it rests on</span>
      </div>
      {decisions.map((d) => {
        const line = lines.find(
          (l) => l.hand_key === d.hand_key && l.decision_key === d.decision_key,
        );
        const measuredHere = line?.baseline_minutes !== null && line?.baseline_minutes !== undefined;
        return (
          <div key={`${d.hand_key}:${d.decision_key}`} className="play-card" data-testid={`ledger-${d.decision_key}`}>
            <div className="play-head">
              <div>
                <div className="play-arch">{d.hand_name}</div>
                <div className="play-title">{d.decision_label}</div>
              </div>
              <div className="play-status">
                {measuredHere ? (
                  <span className={`pill ${(line?.minutes_returned ?? 0) >= 0 ? "green" : "red"}`}>
                    {hours(line?.minutes_returned ?? 0)}
                  </span>
                ) : (
                  <span className="pill gray">not measured</span>
                )}
              </div>
            </div>

            {measuredHere ? (
              <>
                <div className="play-meta">
                  <span>
                    Runs counted <span className="v">{line?.runs ?? 0}</span>
                  </span>
                  <span>
                    Touched by a person <span className="v">{line?.touched ?? 0}</span>
                  </span>
                  {(line?.retroactive ?? 0) > 0 ? (
                    // Counted when the baseline was declared, not when the
                    // run happened. Said, so the two are never confused.
                    <span data-testid={`retroactive-${d.decision_key}`}>
                      Counted after the fact <span className="v">{line!.retroactive}</span>
                    </span>
                  ) : null}
                  <span>
                    First pass{" "}
                    <span className="v">
                      {line?.first_pass_pct === null || line?.first_pass_pct === undefined
                        ? "—"
                        : `${line.first_pass_pct}%`}
                    </span>
                  </span>
                  <span>
                    Baseline <span className="v">{line?.baseline_minutes} min</span>
                  </span>
                  <span>
                    Review <span className="v">{line?.review_minutes} min</span>
                  </span>
                </div>
                {/* The provenance of the number the whole line multiplies. */}
                <div className="play-foot" data-testid={`basis-${d.decision_key}`}>
                  <span>
                    Declared by {line?.declared_by_name ?? "someone no longer here"}
                    {line?.declared_at ? ` on ${formatWhen(line.declared_at)}` : ""}
                  </span>
                  <span style={{ opacity: 0.85 }}>“{line?.source_note}”</span>
                </div>
              </>
            ) : (
              <div className="play-meta">
                <span>
                  Nobody has said what this used to take, so nothing is claimed
                  for it.
                </span>
              </div>
            )}

            {canDeclare ? (
              <DeclareBaseline workspaceId={workspaceId} decision={d} line={line} />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/** 18 → "18 min"; 150 → "2.5 h". */
function waitWords(mins: number): string {
  return mins < 90 ? `${Math.round(mins)} min` : `${Math.round((mins / 60) * 10) / 10} h`;
}

export function Ledger() {
  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <div className="main-inner" data-testid="ledger-error">
          <div className="lede">Could not load the ledger: {error.message}</div>
        </div>
      )}
    >
      <LedgerInner />
    </ErrorBoundary>
  );
}
